import { useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';
import type { SlideInCustomAnimationDirection } from '@elfsight/widget-modal';
import { PopupCloseControl, SlideIn } from '@elfsight/widget-modal';
import { useIsMobile, useSwipe } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { useFilterVideos, usePopup, useThumbnailMode } from '../../../../utils';
import { Content } from '../../content';
import type { LayoutVariationProps } from './types';

export function LayoutSlideIn({
  show,
  elements,
  position,
  controls: { closeButtonVisible, closeButtonColor },
  ariaLabel,
  ...forwardingProps
}: LayoutVariationProps) {
  const {
    verticalOffset,
    horizontalOffset,
    slideInAnimation,
    editMode,
    swipeToCloseOnMobile,
    closeButtonOnHover,
    widgetBackground: { borderRadius = 4 }
  } = useSettings();
  const {
    thumbnailMode,
    styles: {
      margin,
      boxShadow: thumbnailBoxShadow,
      borderRadius: thumbnailBorderRadius,
      overflow,
      backgroundColor,
      maxWidth
    }
  } = useThumbnailMode({ withWidth: true });
  const { close } = usePopup();
  const filteredElements = useFilterVideos(elements, show);
  const [hovered, setHovered] = useState(false);

  const isMobile = useIsMobile();

  // close popup on animation change
  const firstUpdate = useRef(true);
  useEffect(() => {
    if (editMode) {
      if (firstUpdate.current) {
        firstUpdate.current = false;
      } else {
        close();
      }
    }
    // The list of dependencies should be exactly like this
  }, [slideInAnimation]);

  const [swipeToCloseRef, swipeToCloseHandlers, swipeToCloseDirection] =
    useSwipe({ onSwipe: close, excludeDirections: ['Up'] });

  const customDirection = (
    swipeToCloseDirection?.toLowerCase() === 'up'
      ? 'top'
      : swipeToCloseDirection?.toLowerCase() === 'down'
        ? 'bottom'
        : swipeToCloseDirection?.toLowerCase()
  ) as SlideInCustomAnimationDirection;

  const isCloseButtonAvailable = closeButtonOnHover
    ? hovered
    : closeButtonVisible;

  const controlClose = isCloseButtonAvailable ? (
    <PopupCloseControl color={closeButtonColor} onClick={close} />
  ) : null;

  return (
    <SlideIn
      ariaLabel={ariaLabel}
      isOpen={show}
      animation={thumbnailMode ? 'instant' : slideInAnimation}
      position={{
        position,
        verticalOffset,
        horizontalOffset
      }}
      customAnimationDirection={customDirection}
    >
      <ModalContent
        _boxShadow={
          thumbnailMode ? thumbnailBoxShadow : '0 4px 8px 0 rgba(0, 0, 0, 0.15)'
        }
        _borderRadius={thumbnailMode ? thumbnailBorderRadius : borderRadius}
        _overflow={overflow}
        _backgroundColor={backgroundColor}
        _margin={margin}
        _maxWidth={maxWidth}
        _horizontalOffset={horizontalOffset}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        {...(isMobile && swipeToCloseOnMobile
          ? { ...swipeToCloseHandlers, ref: swipeToCloseRef }
          : {})}
      >
        <Content
          {...forwardingProps}
          elements={filteredElements}
          controlClose={controlClose}
          popupShowing={show}
        />
      </ModalContent>
    </SlideIn>
  );
}

const ModalContent = styled.div<{
  _boxShadow?: string;
  _borderRadius?: number;
  _margin?: number;
  _overflow?: CSSProperties['overflow'];
  _backgroundColor?: string;
  _maxWidth?: string;
  _horizontalOffset?: number;
}>`
  box-shadow: ${({ _boxShadow }) => _boxShadow};
  overflow: ${({ _overflow }) => _overflow};
  border-radius: ${({ _borderRadius }) => _borderRadius}px;
  margin: ${({ _margin }) => (_margin ? `${_margin}px` : '0')};
  background-color: ${({ _backgroundColor }) => _backgroundColor};
  max-width: ${({ _maxWidth, _horizontalOffset }) =>
    _maxWidth || `calc(100vw - ${_horizontalOffset || 0}px)`};

  @supports (width: 100dvw) {
    max-width: ${({ _maxWidth, _horizontalOffset }) =>
      _maxWidth || `calc(100dvw - ${_horizontalOffset || 0}px)`};
  }
`;
